<script setup>
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';

import { QUOTE_TEMPLATES } from './quoteTemplates';

defineProps({
  modelValue: {
    type: String,
    default: 'classic',
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
</script>

<template>
  <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
    <button
      v-for="template in QUOTE_TEMPLATES"
      :key="template.key"
      type="button"
      :data-template-key="template.key"
      :aria-label="`Use ${template.name} quote design`"
      :aria-pressed="modelValue === template.key"
      class="group relative rounded-xl border p-2 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9 focus-visible:ring-offset-2"
      :class="
        modelValue === template.key
          ? 'border-n-blue-9 bg-n-blue-2 shadow-sm'
          : 'border-n-slate-6 bg-n-alpha-1 hover:-translate-y-0.5 hover:border-n-slate-8 hover:shadow-sm'
      "
      @click="emit('update:modelValue', template.key)"
    >
      <span
        class="template-thumbnail mb-2.5 block aspect-[1.42] overflow-hidden rounded-lg border border-n-slate-5 bg-white"
        :class="`template-${template.key}`"
        aria-hidden="true"
      >
        <span class="thumbnail-accent" />
        <span class="thumbnail-kicker">{{ t('QUOTES.TEMPLATES.LABEL') }}</span>
        <span class="thumbnail-title" />
        <span class="thumbnail-rule" />
        <span class="thumbnail-row thumbnail-row-wide" />
        <span class="thumbnail-row" />
        <span class="thumbnail-total" />
      </span>

      <span class="flex items-start justify-between gap-2">
        <span class="min-w-0">
          <span class="block text-xs font-medium text-n-slate-12 2xl:text-sm">
            {{ template.name }}
          </span>
          <span class="mt-0.5 hidden truncate text-xs text-n-slate-10 2xl:block">
            {{ template.description }}
          </span>
        </span>
        <span
          v-if="modelValue === template.key"
          class="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-n-blue-9 text-white"
        >
          <Icon icon="i-lucide-check" size="size-3.5" />
        </span>
      </span>
    </button>
  </div>
</template>

<style scoped>
.template-thumbnail {
  position: relative;
  padding: 12px;
}

.template-thumbnail > span {
  position: absolute;
  display: block;
}

.thumbnail-kicker {
  left: 12px;
  top: 12px;
  color: #7c3aed;
  font-size: 5px;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.thumbnail-title {
  left: 12px;
  top: 24px;
  width: 46%;
  height: 6px;
  border-radius: 2px;
  background: #111827;
}

.thumbnail-rule {
  left: 12px;
  right: 12px;
  top: 40px;
  height: 1px;
  background: #e5e7eb;
}

.thumbnail-row {
  left: 12px;
  top: 49px;
  width: 42%;
  height: 3px;
  border-radius: 2px;
  background: #d1d5db;
}

.thumbnail-row-wide {
  top: 57px;
  width: 70%;
}

.thumbnail-total {
  right: 12px;
  bottom: 10px;
  width: 25%;
  height: 7px;
  border-radius: 2px;
  background: #7c3aed;
}

.template-executive {
  background: #f8f6f0;
}

.template-executive .thumbnail-accent {
  inset: 0 0 auto;
  height: 20px;
  background: #14213d;
}

.template-executive .thumbnail-kicker {
  color: #d4a72c;
  top: 7px;
}

.template-executive .thumbnail-title {
  top: 30px;
  background: #14213d;
}

.template-executive .thumbnail-total {
  background: #14213d;
}

.template-studio .thumbnail-accent {
  inset: 0 auto 0 0;
  width: 9px;
  background: #e45b45;
}

.template-studio .thumbnail-kicker {
  color: #e45b45;
  font-size: 7px;
}

.template-studio .thumbnail-title {
  width: 62%;
}

.template-studio .thumbnail-total {
  background: #e45b45;
}

.template-minimal .thumbnail-kicker {
  color: #111827;
  font-weight: 500;
}

.template-minimal .thumbnail-rule {
  background: #111827;
}

.template-minimal .thumbnail-total {
  height: 3px;
  background: #111827;
}
</style>
